import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Layers } from 'lucide-react';
import { AppButton, AppCard, AppCardContent, EmptyState, ProgressBar, StatusBadge } from '../../../ui';
import CardHead from './CardHead';
import ClassControl from './ClassControl';
import { attendanceTone, modeLabel, orderBatches, plural, timeRange } from '../trainerDashboardUtils';

/** How many batches show before "Show all". */
const COLLAPSED_COUNT = 5;

/** One labelled percentage with its bar. The figure is text, so the bar is decoration. */
function BatchMeter({ label, value, color = 'primary' }) {
  return (
    <div className="tw-min-w-0 tw-flex-1">
      <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2 tw-text-xs">
        <span className="tw-text-content-subtle">{label}</span>
        <span className="tw-font-semibold tw-text-content">{value === null || value === undefined ? '—' : `${value}%`}</span>
      </div>
      {value !== null && value !== undefined && (
        <div aria-hidden="true">
          <ProgressBar value={value} color={color} size="sm" className="tw-mt-1.5" />
        </div>
      )}
    </div>
  );
}

function BatchRow({ batch, onStart, onEnd }) {
  const meta = [
    batch.course,
    plural(batch.studentCount || 0, 'student', 'students'),
    timeRange(batch.startTime, batch.endTime),
    modeLabel(batch.mode),
  ].filter(Boolean).join(' · ');

  return (
    <li className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-3 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-py-3.5 first:tw-border-t-0 first:tw-pt-0 last:tw-pb-0">
      <div className="tw-min-w-0 tw-flex-[1_1_16rem]">
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
          <Link
            to={`/i/batch/${batch._id}`}
            className="tw-min-w-0 tw-truncate tw-rounded-sm tw-text-sm tw-font-semibold tw-text-content tw-no-underline tw-focus-ring hover:tw-text-primary"
          >
            {batch.name}
          </Link>
          {batch.isLive && <StatusBadge status="live" domain="batch" />}
        </div>
        {meta && <p className="tw-m-0 tw-mt-0.5 tw-truncate tw-text-xs tw-text-content-muted">{meta}</p>}
      </div>

      <div className="tw-flex tw-min-w-[12rem] tw-flex-[0_1_18rem] tw-gap-5">
        <BatchMeter
          label="Attendance"
          value={batch.attendancePct}
          color={attendanceTone(batch.attendancePct) || 'primary'}
        />
        <BatchMeter label="Progress" value={batch.progressPct} />
      </div>

      <div className="tw-ml-auto tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-2">
        <ClassControl batch={batch} onStart={onStart} onEnd={onEnd} />
      </div>
    </li>
  );
}

/**
 * BatchesCard — every ongoing batch the trainer teaches, one row each: who is in
 * it, whether they are turning up, how far through it is, and its class control.
 *
 * Progress is the server's date-based figure (how much of start → end has
 * passed), not syllabus coverage. Attendance is null until a class has ended.
 *
 * Props:
 *   batches — the summary's `batches` (ongoing only)
 *   onStart / onEnd — live-class handlers from the page
 */
const BatchesCard = ({ batches = [], onStart, onEnd }) => {
  const titleId = useId();
  const listId = useId();
  const [expanded, setExpanded] = useState(false);

  const ordered = orderBatches(batches);
  const visible = expanded ? ordered : ordered.slice(0, COLLAPSED_COUNT);

  return (
    <AppCard as="section" aria-labelledby={titleId}>
      <CardHead
        id={titleId}
        title="My batches"
        description={batches.length ? `${batches.length} ongoing` : undefined}
      />
      <AppCardContent>
        {ordered.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="No ongoing batches"
            description="Batches you teach appear here once they start."
          />
        ) : (
          <>
            <ul id={listId} className="tw-m-0 tw-list-none tw-p-0">
              {visible.map((b) => (
                <BatchRow key={b._id} batch={b} onStart={onStart} onEnd={onEnd} />
              ))}
            </ul>
            {ordered.length > COLLAPSED_COUNT && (
              <AppButton
                size="sm"
                variant="ghost"
                className="tw-mt-3"
                aria-expanded={expanded}
                aria-controls={listId}
                onClick={() => setExpanded((v) => !v)}
              >
                <ChevronDown
                  size={16}
                  aria-hidden="true"
                  className={expanded ? 'tw-rotate-180 tw-transition-transform' : 'tw-transition-transform'}
                />
                {expanded ? 'Show fewer' : `Show all ${ordered.length} batches`}
              </AppButton>
            )}
          </>
        )}
      </AppCardContent>
    </AppCard>
  );
};

export default BatchesCard;
